import {Navigate, Route} from 'react-router-dom';
import {apiDocsPlugin, ApiExplorerPage} from '@backstage/plugin-api-docs';
import {CatalogEntityPage, CatalogIndexPage, catalogPlugin,} from '@backstage/plugin-catalog';
import {CatalogImportPage, catalogImportPlugin,} from '@backstage/plugin-catalog-import';
import {ScaffolderFieldExtensions} from '@backstage/plugin-scaffolder-react';
import {ScaffolderPage, scaffolderPlugin} from '@backstage/plugin-scaffolder';
import {orgPlugin} from '@backstage/plugin-org';
import {SearchPage} from '@backstage/plugin-search';
import {TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage,} from '@backstage/plugin-techdocs';
import {TechDocsAddons} from '@backstage/plugin-techdocs-react';
import {ReportIssue} from '@backstage/plugin-techdocs-module-addons-contrib';
import {apis} from './apis';
import {searchPage} from './components/search/SearchPage';
import {Root} from './components/Root';

import {AlertDisplay, OAuthRequestDialog, SignInPage,} from '@backstage/core-components';
import {createApp} from '@backstage/app-defaults';
import {AppRouter, FlatRoutes} from '@backstage/core-app-api';
import {CatalogGraphPage} from '@backstage/plugin-catalog-graph';
import {RequirePermission} from '@backstage/plugin-permission-react';
import {catalogEntityCreatePermission} from '@backstage/plugin-catalog-common/alpha';
import {providers} from './components/signin/identityProviders';
import {translation} from './translation';
import {PrefixedInputFieldExtension} from './scaffolder/extensions';
import {PequiChatbotWidgetWrapper} from '@internal/plugin-pequi-chatbot-widget';
import {PequiDashboardPage} from '@internal/plugin-pequi-dashboard';
import {UserSettingsPage} from "@backstage/plugin-user-settings";
import {NotificationsPage} from '@backstage/plugin-notifications';
import {appEntityPage} from './components/catalog/AppEntityPage.tsx';
import {AppTemplateCardComponent} from "./components/shared/AppTemplateCardComponent.tsx";
import { RbacPage } from '@backstage-community/plugin-rbac';

const app = createApp({
    plugins: [],
    apis,
    bindRoutes({bind}) {
        bind(catalogPlugin.externalRoutes, {
            createComponent: scaffolderPlugin.routes.root,
            viewTechDoc: techdocsPlugin.routes.docRoot,
            createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
        });
        bind(apiDocsPlugin.externalRoutes, {
            registerApi: catalogImportPlugin.routes.importPage,
        });
        bind(scaffolderPlugin.externalRoutes, {
            registerComponent: catalogImportPlugin.routes.importPage,
            viewTechDoc: techdocsPlugin.routes.docRoot,
        });
        bind(orgPlugin.externalRoutes, {
            catalogIndex: catalogPlugin.routes.catalogIndex,
        });
    },
    components: {
        SignInPage: props => <SignInPage {...props} auto providers={providers}/>,
    },
    __experimentalTranslations: {
        availableLanguages: ['en', 'pt-br'],
        resources: [...translation],
    },
});

const routes = (
    <FlatRoutes>
        <Route path="/" element={<Navigate to="home"/>}/>
        <Route path="/home" element={<PequiDashboardPage/>}/>

        <Route path="/catalog" element={<CatalogIndexPage/>}/>
        <Route
            path="/catalog/:namespace/:kind/:name"
            element={<CatalogEntityPage/>}
        >
            {appEntityPage}
        </Route>
        <Route path="/docs" element={<TechDocsIndexPage/>}/>
        <Route
            path="/docs/:namespace/:kind/:name/*"
            element={<TechDocsReaderPage/>}
        >
            <TechDocsAddons>
                <ReportIssue/>
            </TechDocsAddons>
        </Route>
        <Route
            path="/create"
            element={
                <ScaffolderPage
                    components={{
                        TemplateCardComponent: AppTemplateCardComponent,
                    }}
                />
            }
        >
            <ScaffolderFieldExtensions>
                <PrefixedInputFieldExtension/>
            </ScaffolderFieldExtensions>
        </Route>
        <Route path="/api-docs" element={<ApiExplorerPage/>}/>
        <Route
            path="/catalog-import"
            element={
                <RequirePermission permission={catalogEntityCreatePermission}>
                    <CatalogImportPage/>
                </RequirePermission>
            }
        />
        <Route path="/search" element={<SearchPage/>}>
            {searchPage}
        </Route>
        <Route path="/settings" element={<UserSettingsPage/>}/>
        <Route path="/catalog-graph" element={<CatalogGraphPage/>}/>
        <Route path="/notifications" element={<NotificationsPage/>}/>
        <Route path="/rbac" element={<RbacPage />} />;
    </FlatRoutes>
);

export default app.createRoot(
    <>
        <AlertDisplay/>
        <OAuthRequestDialog/>
        <AppRouter>
            <Root>{routes}</Root>
        </AppRouter>
        <PequiChatbotWidgetWrapper/>
    </>,
);
